type FileSystemEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
};

type FileSystemFileEntry = FileSystemEntry & {
  file: (
    successCallback: (file: File) => void,
    errorCallback?: (error: DOMException) => void,
  ) => void;
};

type FileSystemDirectoryEntry = FileSystemEntry & {
  createReader: () => FileSystemDirectoryReader;
};

type FileSystemDirectoryReader = {
  readEntries: (
    successCallback: (entries: FileSystemEntry[]) => void,
    errorCallback?: (error: DOMException) => void,
  ) => void;
};

export function hasFileDrag(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes('Files');
}

async function readAllDirectoryEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  const entries: FileSystemEntry[] = [];

  const readBatch = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });

  let batch = await readBatch();
  while (batch.length > 0) {
    entries.push(...batch);
    batch = await readBatch();
  }

  return entries;
}

async function traverseFileTreeEntry(
  entry: FileSystemEntry,
  files: File[],
): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => {
      (entry as FileSystemFileEntry).file(resolve, reject);
    });
    files.push(file);
    return;
  }

  if (!entry.isDirectory) {
    return;
  }

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const childEntries = await readAllDirectoryEntries(reader);

  for (const childEntry of childEntries) {
    await traverseFileTreeEntry(childEntry, files);
  }
}

export async function collectFilesFromDataTransfer(
  dataTransfer: DataTransfer,
): Promise<File[]> {
  const items = dataTransfer.items;

  if (items && items.length > 0) {
    const rootEntries: FileSystemEntry[] = [];

    for (const item of Array.from(items)) {
      if (item.kind !== 'file') {
        continue;
      }

      const entry = item.webkitGetAsEntry?.() ?? null;
      if (entry) {
        rootEntries.push(entry);
      }
    }

    if (rootEntries.length > 0) {
      const files: File[] = [];
      for (const entry of rootEntries) {
        await traverseFileTreeEntry(entry, files);
      }
      return files;
    }
  }

  return Array.from(dataTransfer.files);
}
